/* eslint-disable no-console */
import {LockFilled} from '@ant-design/icons';
import {Divider, Flex} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

import {ReactComponent as ReductionDownArrow} from '../../../../assets/icons/ReductionDownArrow.svg';
import reductionNoDataChart from '../../../../assets/skeleton/reductionNoDataChart.png';
import reductionRoadMapBg from '../../../../assets/skeleton/reductionRoadMap.png';
import UpgradeButton from '../../../../components/UpgradeButton/UpgradeButton';
import {Routes} from '../../../../constants/Routes';
import {subscriptionPlanTypes} from '../../../../constants/subscriptionConstants';
import {eachWordCap} from '../../../../utils/FormUtils/FirstCap';
import {getSubscriptionPlanType} from '../../../../utils/SubscriptionHelper';

import {ScenarioEnum} from './IndustryBenchmarks/utils';

const ReductionChart: React.FC<{
  selectedYear: string;
  reductionData: any;
  reductionTimelineData: any;
}> = ({selectedYear, reductionData, reductionTimelineData}) => {
  const navigate = useNavigate();
  const isFreePlan = getSubscriptionPlanType() === subscriptionPlanTypes.FREE;
  const hasReductionData =
    Array.isArray(reductionData) &&
    reductionData.some(
      (item: {
        expectedEmissions?: number | null;
        actualEmissions?: number | null;
      }) => item?.expectedEmissions != null || item?.actualEmissions != null
    );

  const yMax = reductionData?.length
    ? reductionData?.reduce(
        (
          max: number,
          d: {expectedEmissions?: number; actualEmissions?: number}
        ) => {
          const expected = d.expectedEmissions ?? 0;
          const actual = d.actualEmissions ?? 0;

          return Math.max(max, expected, actual);
        },
        0
      )
    : 0;

  const sbti = 'sbti';

  const reductioNPerYearLabel =
    reductionTimelineData?.selectScenario ===
    ScenarioEnum.ABSOLUTE_WELL_BELOW_2C
      ? '2.5'
      : '4.2';
  const showLockedState = isFreePlan;
  const showChartState = !showLockedState && hasReductionData;
  const showNoDataState = !showLockedState && !hasReductionData;

  return (
    <div className="reduction-chart-wrapper">
      <div className="reduction-chart-header">
        {!isFreePlan && (
          <Flex className="totalGHGEmissionsTitle">
            {reductionTimelineData?.selectReductionTarget === sbti
              ? reductionTimelineData?.selectScenario
              : eachWordCap(reductionTimelineData?.selectReductionTarget ?? '')}
          </Flex>
        )}
        <div className="reduction-chart-legend-spacer" aria-hidden />
        {!isFreePlan &&
          reductionTimelineData?.selectReductionTarget === sbti &&
          reductionTimelineData !== null && (
            <div className="reduction-per-year-block">
              <Flex vertical={false} align="baseline" gap={8}>
                <div>
                  <ReductionDownArrow />
                </div>
                <div>
                  <span className="reduction-per-year-value">
                    {reductioNPerYearLabel}
                  </span>
                  <span className="reduction-per-year-unit">% </span>
                </div>
              </Flex>
              <span className="reduction-per-year-label">
                Reduction per year
              </span>
            </div>
          )}
      </div>
      <Divider />
      {reductionTimelineData && showChartState && (
        <div
          style={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: '10px',
            justifyContent: 'flex-end',
            gap: '10px',
            marginRight: '30px',
            fontWeight: 500,
            fontSize: 12,
            fontSmooth: 'always'
          }}
        >
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#20E2BD',
                borderRadius: '50%'
              }}
            ></div>
            <span
              style={{
                fontSize: '10px',
                color: '#557F97',
                marginLeft: '5px'
              }}
            >
              Expected Reduction
            </span>
          </div>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#9fd983',
                borderRadius: '50%'
              }}
            ></div>
            <span
              style={{
                fontSize: '10px',
                color: '#557F97',
                marginLeft: '5px'
              }}
            >
              Actual Reduction
            </span>
          </div>
        </div>
      )}
      {showChartState ? (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={reductionData}
            margin={{top: 40, right: 20, bottom: 40, left: 40}}
          >
            <defs>
              <linearGradient
                id="reductionExpectedFill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#024064" stopOpacity={1} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0.45} />
              </linearGradient>
              <linearGradient
                id="reductionActualFill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#05DFAD" stopOpacity={1} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0.45} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="year"
              padding={{left: 30, right: 30}}
              label={{
                value: 'Year',
                position: 'insideBottom',
                offset: -10,
                style: {
                  fontSize: '14px',
                  fontWeight: 'bold'
                }
              }}
            />
            <YAxis
              label={{
                value: 'tCO2e',
                angle: -90,
                position: 'insideLeft',
                offset: -5,
                dy: 23,
                style: {
                  fontSize: '14px',
                  fontWeight: 'bold'
                }
              }}
            />
            <Tooltip
              labelFormatter={(label) => `Year : ${label}`}
              formatter={(value: number) => `${value.toFixed(2)}  tCO2e`}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{opacity: 0, pointerEvents: 'none'}}
            />
            <Area
              type="linear"
              dataKey="expectedEmissions"
              name="Expected Reduction"
              stroke="#20E2BD"
              fill="#EEF2FF80"
              strokeWidth={2}
            />
            <Area
              type="linear"
              dataKey="actualEmissions"
              name="Actual Reduction"
              stroke="#9fd983"
              fill="#EEF2FF80"
              strokeWidth={2}
            />
            <ReferenceLine x={selectedYear.toString()} stroke="#AABCC7" />
            <ReferenceLine
              y={yMax / 2}
              stroke="#ADAFB7"
              strokeDasharray="3 3"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : showLockedState ? (
        <div
          className="reduction-chart-empty-state industryBenchmarkCard--locked"
          style={{
            backgroundImage: `url(${reductionRoadMapBg})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center'
          }}
        >
          <Flex
            className="industryBenchmarkLockedWrap"
            vertical
            align="center"
            gap={18}
          >
            <div className="industryBenchmarkLockIconWrap">
              <LockFilled className="industryBenchmarkLockIcon" />
            </div>
            <Flex vertical align="center" gap={2}>
              <span className="industryBenchmarkLockedTitle">
                Unlock Advanced
              </span>
              <span className="industryBenchmarkLockedSubtitle">
                Reduction Road Map insights
              </span>
            </Flex>
            <div
              onClick={() => {
                navigate(Routes.PRICING_DETAILS);
              }}
            >
              <UpgradeButton
                title="Upgrade"
                style={{
                  height: 29,
                  minWidth: 102,
                  borderRadius: 4,
                  font: 'normal normal 700 14px Montserrat',
                  color: '#1B1D2A'
                }}
              />
            </div>
          </Flex>
        </div>
      ) : showNoDataState ? (
        <div className="reduction-chart-no-data-state">
          <div className="reduction-chart-no-data-content">
            <img src={reductionNoDataChart} alt="No reduction data available" />
            <span className="reduction-chart-no-data-title">No Data Yet</span>
            <span className="reduction-chart-no-data-subtitle">
              We have not computed any data from your end yet...
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReductionChart;
